import React, { useMemo, useState } from "react";
import { PlusCircle, RefreshCcwIcon, SquareX, Trash } from "lucide-react";
import type { Store } from "../../../types/store";
import StoreCard from "../../../components/StoreCard";
import AddEditStoreModal from "../../../components/Forms/AddEditStoreModal";
import AdminLayout from "../Layout";
import { useStores } from "../../../hooks/useStores";
import { StoreCardSkeleton } from "../../../components/skeletons/StoreCardSkeleton";
import { useAppDispatch } from "../../../hooks";
import { createStore, deleteStores, getStores } from "../../../slices/storeSlice";
import DeletionConfirmationModal from "../../../components/DeletionConfirmationModal";
import { useSubscriptionGuard } from "../../../hooks/useSubscriptionGuard";
import EntityLimitCard from "../../../components/EntityLimitCard";
import SubscriptionGuardModal from "../../../components/SubscriptionGuardModal";

// const FILTERS = ["today", "this_week", "this_month", "this_quarter", "this_year"] as const;
// type FilterKey = typeof FILTERS[number];

const AdminStoresPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { stores, loading, error } = useStores();
  const { usageOverview, permissions, isMaxStoreCountReached } = useSubscriptionGuard();

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  // const [filter, setFilter] = useState<FilterKey>("this_month");

  // MODAL VISIBILITY STATES
  const [addOpen, setAddOpen] = useState(false);
  const [editStore, setEditStore] = useState<Store | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSubscriptionGuardModal, setShowSubscriptionGuardModal] = useState(false);


  // --- Derived state: filters applied directly from Redux store ---
  // const computeSalesForRange = (base: number, rangeKey: FilterKey) => {
  //   const multipliers: Record<FilterKey, number> = {
  //     today: 0.05,
  //     this_week: 0.2,
  //     this_month: 1,
  //     this_quarter: 3,
  //     this_year: 12,
  //   };
  //   return Math.round(base * (multipliers[rangeKey] || 1));
  // };

  // Filtered & adjusted stores computed from Redux `stores`
  const filteredStores = useMemo(() => {
    return stores.map((s) => ({
      ...s,
      // sales_count: computeSalesForRange(s.salesCount ?? 0, filter),
    }));
  }, [stores]);

  // --- Selection logic ---
  const toggleSelect = (id: string, checked: boolean) => {
    setSelectedIds((prev) =>
      checked ? [...prev, id] : prev.filter((p) => p !== id)
    );
  };

  const toggleSelectAll = (checked: boolean) => {
    setSelectedIds(checked ? filteredStores.map((s) => s.id) : []);
  };

  const deleteSelected = async () => {
    if (selectedIds.length === 0) return;

    // if (!confirm(`Delete ${selectedIds.length} store(s)? This cannot be undone.`)) return;

    const result = await dispatch(deleteStores(selectedIds));

    if (deleteStores.fulfilled.match(result)) {
      console.log(result.payload);
    } else {
      console.error("Deletion failed:", result.payload);
    }
    setSelectedIds([]);
  };

  // --- CRUD handlers ---
  const handleAdd = async (store: any) => {
    await dispatch(createStore(store));
    setAddOpen(false);
  };

  // const handleEdit = (s: Store) => {
  //   // dispatch(updateStore(s)) if you have an update thunk
  //   // or just optimistically update
  //   // Example: dispatch(editStoreLocal(s))
  //   console.log("Store: ", s);
  // };

  const openEdit = (s: Store) => setEditStore(s);
  // const clearFilter = () => setFilter("this_month");

  // console.log("Stores: ", stores);

  return (
    <AdminLayout>
      <div className="p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">

            {/* LARGE SCREEN FILTERS */}
            {/* <div className="hidden sm:flex items-center gap-2 text-sm text-gray-500">
                        <span>Filter:</span>
                        <select 
                            value={filter} 
                            onChange={(e) => setFilter(e.target.value as FilterKey)} 
                            className="form-input text-sm"
                        >
                            <option value="today">Today</option>
                            <option value="this_week">This Week</option>
                            <option value="this_month">This Month</option>
                            <option value="this_quarter">This Quarter</option>
                            <option value="this_year">This Year</option>
                        </select>
                        <button onClick={clearFilter} className="text-sm text-gray-600 underline">Reset</button>
                    </div> */}

            {/* CURRENT STORE COUNT */}
            <EntityLimitCard
              limit={permissions?.storeLimit as number}
              limitReached={isMaxStoreCountReached() as boolean}
              value={usageOverview?.storeCount as number}
              entity="stores"
            />

          </div>


          {/* ACTION BUTTONS (REFRESH, DELETE, ADD) */}
          <div className="flex items-center lg:justify-end gap-2 text-xs">
            <button
              onClick={() => dispatch(getStores())}
              className="px-2 py-2 rounded-md bg-transparent text-gray-800 border border-gray-200"
            >
              <RefreshCcwIcon />
            </button>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                onChange={(e) => toggleSelectAll(e.target.checked)}
                checked={selectedIds.length === stores.length && stores.length > 0}
                aria-label="Select all stores"
              />
              <button
                disabled={!selectedIds.length}
                onClick={() => setShowDeleteModal(true)}
                className={`px-2 py-2 rounded-md ${selectedIds.length ? "bg-red-600 text-white" : "bg-gray-200 text-gray-500"}`}
              >
                <Trash className="inline mr-2" /> Delete {selectedIds.length > 0 && `(${selectedIds.length})`}
              </button>
            </div>

            <button
              onClick={() => {
                if (isMaxStoreCountReached()) {
                  setShowSubscriptionGuardModal(true);
                } else {
                  setAddOpen(true);
                }
              }}
              className="px-2 py-2 rounded-md bg-primary text-white flex items-center gap-2"
            >
              <PlusCircle /> Add
            </button>
          </div>
        </div>

        {/* Mobile filter row */}
        {/* <div className="sm:hidden flex gap-2 mb-4 overflow-auto pb-1">
                {FILTERS.map((f) => (
                <button
                    key={f}
                    onClick={() => setFilter(f)}
                    className={`px-2 py-1 rounded-md text-sm text-gray-600 ${filter === f ? "bg-primary text-white" : "bg-white/80"}`}
                >
                    {f.replace("_", " ")}
                </button>
                ))}
            </div> */}

        {/* Grid */}
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {loading ? (
            Array.from({ length: 6 }).map((_, i) => <StoreCardSkeleton key={i} />)
          ) : stores.length > 0 ? (
            filteredStores.map((s: Store) => (
              <StoreCard
                key={s.id}
                store={s}
                selected={selectedIds.includes(s.id)}
                onSelect={toggleSelect}
                onEdit={openEdit}
              />
            ))
          ) : (
            <div className="flex flex-col items-center justify-center text-gray-400 col-span-full py-10 gap-3">
              <SquareX size={56} />
              <h1 className="text-gray-600 mt-2 text-center">
                No stores registered yet
              </h1>

              <button
                onClick={() => {
                  if (isMaxStoreCountReached()) {
                    setShowSubscriptionGuardModal(true);
                  } else {
                    setAddOpen(true);
                  }
                }}
                className="px-2 py-2 rounded-md bg-primary text-white flex items-center gap-2"
              >
                <PlusCircle /> Add your first Store
              </button>
            </div>
          )}
        </div>

        {error && (
          <div className="flex items-center justify-between bg-red-50 text-red-600 border border-red-200 p-3 rounded-md mb-4">
            <span>{error}</span>
            <button
              onClick={() => dispatch(getStores())}
              className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
            >
              Retry
            </button>
          </div>
        )}

        {/* ADD, EDIT, DELETE MODALS */}
        <AddEditStoreModal
          open={addOpen}
          initial={null}
          onClose={() => setAddOpen(false)}
          onSave={handleAdd}
        />

        <AddEditStoreModal
          open={!!editStore}
          initial={editStore ?? null}
          onClose={() => setEditStore(null)}
          onSave={() => setEditStore(null)}
        />

        <DeletionConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false)
            setSelectedIds([])
          }}
          onConfirm={() => {
            setShowDeleteModal(false)
            deleteSelected()
          }}
          count={selectedIds.length}
          type="store"
        />

        {/* SUBSCRIPTION GUARD */}
        <SubscriptionGuardModal
          isOpen={showSubscriptionGuardModal}
          onClose={() => setShowSubscriptionGuardModal(false)}
          type="stores"
        />
      </div>
    </AdminLayout>
  );
};

export default AdminStoresPage;
